import { useEffect } from 'react';
import { trackEvent } from '../utils/gtm';

/**
 * Self-healing for the "dark screen, nothing clickable, only refresh helps"
 * class of bugs on mobile.
 *
 * A MUI modal can strand its backdrop (dark) or its root (an invisible
 * full-screen click blocker) when a transition runs while the page is
 * hidden/backgrounded, after a bfcache restore, or when open/close races a
 * route change. Either way the user is stuck behind a layer they can't see
 * past or tap through.
 *
 * Detection is conservative and continuous:
 * - A modal is a candidate only when its root is painted but NO content
 *   inside it is visible in the viewport. Legitimate open dialogs/drawers
 *   always have visible content; keepMounted closed modals have
 *   visibility:hidden roots, so neither is ever touched.
 * - A candidate must stay stuck for two consecutive scans (~4s) before it is
 *   released, so enter/exit transitions are never interrupted.
 *
 * Recovery hides the stuck root and releases MUI's body scroll lock. If
 * React later re-renders that modal (e.g. the user re-opens the dialog), a
 * MutationObserver hands control back by removing our overrides.
 */

const SCAN_INTERVAL_MS = 2000;
const STRIKES_TO_RELEASE = 2;

function isVisiblyPainted(el) {
    const cs = window.getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    return Number(cs.opacity || 1) > 0.05;
}

function hasVisibleContent(modal) {
    const contentNodes = Array.from(modal.children).filter(
        (child) => !child.classList.contains('MuiBackdrop-root') && child.tagName !== 'SPAN'
    );
    return contentNodes.some((node) => {
        if (!isVisiblyPainted(node)) return false;
        const rect = node.getBoundingClientRect();
        const intersectsViewport = rect.right > 0 && rect.left < window.innerWidth
            && rect.bottom > 0 && rect.top < window.innerHeight;
        return rect.width > 4 && rect.height > 4 && intersectsViewport;
    });
}

/** Hand control back to React if it re-renders a released modal later. */
function restoreOnReactUpdate(modal) {
    const observer = new MutationObserver((mutations) => {
        const external = mutations.some(
            (m) => !(m.target === modal && m.type === 'attributes' && m.attributeName === 'style')
        );
        if (!external) return;
        observer.disconnect();
        delete modal.dataset.elWatchdogReleased;
        delete modal.dataset.elWatchdogStrikes;
        modal.style.removeProperty('display');
        modal.style.removeProperty('pointer-events');
    });
    observer.observe(modal, { attributes: true, childList: true, subtree: true });
}

function reportRelease(modal) {
    try {
        const content = Array.from(modal.children).find(
            (child) => !child.classList.contains('MuiBackdrop-root') && child.tagName !== 'SPAN'
        );
        const detail = {
            overlay_class: String(modal.className || '').slice(0, 180),
            content_class: String(content?.className || '').slice(0, 180),
            path: window.location.pathname,
        };
        // eslint-disable-next-line no-console
        console.warn('[EmailLion] released stuck overlay', detail);
        trackEvent('stuck_overlay_released', detail);
    } catch { /* telemetry must never break recovery */ }
}

function scanOnce() {
    const modals = document.querySelectorAll('.MuiModal-root');
    let releasedAny = false;

    modals.forEach((modal) => {
        if (modal.dataset.elWatchdogReleased) return;

        // Painted root = mounted and not hidden; it intercepts every tap even
        // when its backdrop is transparent or fully faded out.
        if (!isVisiblyPainted(modal) || hasVisibleContent(modal)) {
            delete modal.dataset.elWatchdogStrikes;
            return;
        }

        const strikes = Number(modal.dataset.elWatchdogStrikes || 0) + 1;
        if (strikes < STRIKES_TO_RELEASE) {
            modal.dataset.elWatchdogStrikes = String(strikes);
            return;
        }

        modal.dataset.elWatchdogReleased = '1';
        modal.style.setProperty('display', 'none', 'important');
        modal.style.setProperty('pointer-events', 'none', 'important');
        restoreOnReactUpdate(modal);
        reportRelease(modal);
        releasedAny = true;
    });

    if (releasedAny) {
        // Release MUI's scroll lock only if no other visible modal remains.
        const stillOpen = Array.from(document.querySelectorAll('.MuiModal-root'))
            .some((m) => !m.dataset.elWatchdogReleased && isVisiblyPainted(m));
        if (!stillOpen) {
            document.body.style.removeProperty('overflow');
            document.body.style.removeProperty('padding-right');
        }
    }
}

const StuckOverlayWatchdog = () => {
    useEffect(() => {
        const interval = window.setInterval(() => {
            if (document.visibilityState === 'hidden') return;
            scanOnce();
        }, SCAN_INTERVAL_MS);

        // Extra immediate-ish checks on the transitions most likely to strand
        // an overlay: bfcache restore and returning to a backgrounded tab.
        const check = () => window.setTimeout(scanOnce, 1200);
        const onPageShow = (e) => { if (e.persisted) check(); };
        const onVisibility = () => {
            if (document.visibilityState === 'visible') check();
        };

        window.addEventListener('pageshow', onPageShow);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('pageshow', onPageShow);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    return null;
};

export default StuckOverlayWatchdog;
